<script lang="ts" setup>
import { computed, toRefs } from "vue";
import { ForwardSlots } from "vue-forward-slots";
import { VSelect } from "vuetify/components";

import { pluralize } from "#utilities/typography";
import { type FieldInputProps, useFormInput } from "./use-form-input";

interface SelectFieldProps extends FieldInputProps {
  chips?: boolean;
}

const props = withDefaults(defineProps<SelectFieldProps>(), {
  chips: false,
  disabled: false,
  hint: "",
  label: undefined,
  type: "text",
});

// NOTE: Must redefine expected Vuetify "child" slots for types to work with 'vue-forward-slots'!
defineSlots<VSelect["$slots"]>();

defineOptions({
  // Must prevent attributes from falling through to 'ForwardSlots' (instead bind to Vuetify component)!
  inheritAttrs: false,
});

const { disabled, error, field } = useFormInput(toRefs(props));
const { value: formValue, handleBlur, handleChange } = field;

const selectedCount = computed(() =>
  Array.isArray(formValue.value) ? formValue.value.length : formValue.value ? 1 : 0,
);

const maxDisplayCount = 1;
</script>

<template>
  <ForwardSlots :slots="$slots">
    <VSelect
      v-bind="$attrs"
      :disabled="disabled || props.disabled"
      :error="!!error"
      :hint="error ?? hint"
      :label="label"
      :model-value="formValue"
      @blur="handleBlur"
      @update:model-value="handleChange"
    >
      <template #selection="{ item, index }">
        <template v-if="index < maxDisplayCount">
          <VChip v-if="chips" :text="item.title" />
          <span v-else class="inline-flex">
            {{ item.title }}{{ index < maxDisplayCount - 1 ? "," : "" }}
          </span>
        </template>
        <span
          v-else-if="index === maxDisplayCount"
          class="text-disabled text-caption align-self-center"
        >
          (+{{ selectedCount - maxDisplayCount }})
        </span>
      </template>
    </VSelect>
  </ForwardSlots>
</template>
